import { useEffect, useRef, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { resetInstitutionCache } from "@/lib/resultDocument";
import { Loader2, Upload, Trash2, X, Building2, Save } from "lucide-react";

export default function InstitutionModal({ onClose }) {
  const [name, setName] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [hasLogo, setHasLogo] = useState(false);
  const [logoV, setLogoV] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const base = process.env.REACT_APP_BACKEND_URL;

  const apply = (data) => {
    setName(data.name || "");
    setSubtitle(data.subtitle || "");
    setHasLogo(!!data.has_logo);
    setLogoV(data.logo_v || "");
    resetInstitutionCache();
  };

  useEffect(() => {
    api.get("/settings/institution").then(({ data }) => apply(data)).catch((e) => toast.error(apiErr(e)));
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/settings/institution", { name, subtitle });
      apply(data);
      toast.success("Kurum bilgileri kaydedildi");
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/settings/institution/logo", fd);
      apply(data);
      toast.success("Logo yüklendi");
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const removeLogo = async () => {
    if (!window.confirm("Logo kaldırılsın mı?")) return;
    try {
      const { data } = await api.delete("/settings/institution/logo");
      apply(data);
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" data-testid="institution-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <div>
            <h2 className="font-display text-lg font-bold text-slate-900 flex items-center gap-2"><Building2 size={18} /> Kurum Bilgileri</h2>
            <p className="text-xs text-slate-500 mt-0.5">Sonuç belgelerinin başlığında görünecek logo ve kurum adı.</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="institution-close-btn"><X size={20} /></button>
        </div>
        <div className="p-4 space-y-4">
          <div className="flex items-center gap-4">
            <div className="w-24 h-24 rounded-xl border border-dashed border-slate-300 bg-slate-50 flex items-center justify-center overflow-hidden" data-testid="institution-logo-preview">
              {hasLogo ? <img src={`${base}/api/settings/institution/logo?v=${encodeURIComponent(logoV)}`} alt="Kurum logosu" className="max-w-full max-h-full object-contain" /> : <Building2 size={28} className="text-slate-300" />}
            </div>
            <div className="space-y-2">
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="institution-logo-input" />
              <Button size="sm" variant="outline" disabled={busy} onClick={() => fileRef.current?.click()} className="h-8 text-xs" data-testid="institution-logo-upload-btn">
                {busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} <span className="ml-1">{hasLogo ? "Logoyu Değiştir" : "Logo Yükle"}</span>
              </Button>
              {hasLogo && (
                <button onClick={removeLogo} className="text-xs text-rose-600 hover:underline flex items-center gap-1" data-testid="institution-logo-remove-btn"><Trash2 size={12} /> Logoyu kaldır</button>
              )}
              <p className="text-[11px] text-slate-400">PNG/JPG, en fazla 3 MB.</p>
            </div>
          </div>
          <div>
            <label className="ta-label">Kurum Adı</label>
            <input className="ta-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Örn: ZOR AKADEMİ" data-testid="institution-name-input" />
          </div>
          <div>
            <label className="ta-label">Alt Başlık (opsiyonel)</label>
            <input className="ta-input" value={subtitle} onChange={(e) => setSubtitle(e.target.value)} placeholder="Örn: Eğitim Kurumu" data-testid="institution-subtitle-input" />
          </div>
          <Button onClick={save} disabled={busy} className="w-full bg-slate-900 hover:bg-slate-800" data-testid="institution-save-btn">
            <Save size={15} /> <span className="ml-1">Kaydet</span>
          </Button>
        </div>
      </div>
    </div>
  );
}
